/**
 * TagsModal Component
 *
 * Modal for adding tags to a contact. Only allows adding new tags,
 * removal is done via X buttons on the tag pills in the right panel.
 */

import React, {FC, useState, useEffect, useCallback, useMemo} from 'react'
import {Modal} from 'react-bootstrap'
import Select, {components, MenuListProps, MultiValue, StylesConfig, GroupBase} from 'react-select'
import {KTSVG} from '../../../../../../_metronic/helpers'
import {useContactTags, TagOption} from '../../hooks/useContactTags'
import styles from './TagsModal.module.scss'

// ============================================================================
// Types
// ============================================================================

interface TagsModalProps {
  show: boolean
  onHide: () => void
  contactId: string | null
  currentTags: Array<{name: string; isHidden?: boolean; type?: string; _id?: string}>
  onTagsUpdated: () => void
}

// ============================================================================
// Custom Components for react-select
// ============================================================================

const createCustomMenuList = (onCreateClick: () => void) => {
  const CustomMenuList = (props: MenuListProps<TagOption, true, GroupBase<TagOption>>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <div className={styles.createNewOption} onClick={onCreateClick}>
          <i className='bi bi-plus-lg'></i>
          <span>Create new tag</span>
        </div>
      </components.MenuList>
    )
  }
  return CustomMenuList
}

// Custom styles for react-select
const selectStyles: StylesConfig<TagOption, true> = {
  menuPortal: (base) => ({...base, zIndex: 9999}),
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#8950fc' : '#e4e6ef',
    boxShadow: state.isFocused ? '0 0 0 1px #8950fc' : 'none',
    '&:hover': {
      borderColor: '#8950fc',
    },
    minHeight: '42px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#8950fc' : state.isFocused ? '#f8f6ff' : 'transparent',
    color: state.isSelected ? '#fff' : '#181c32',
    cursor: 'pointer',
    '&:active': {
      backgroundColor: '#8950fc',
    },
    '&:hover': {
      color: '#8950fc',
    },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#f8f6ff',
    borderRadius: '12px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#8950fc',
    fontSize: '12px',
    padding: '2px 8px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#8950fc',
    borderRadius: '0 12px 12px 0',
    '&:hover': {
      backgroundColor: '#8950fc',
      color: '#fff',
    },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#a1a5b7',
  }),
}

// ============================================================================
// Component
// ============================================================================

const TagsModal: FC<TagsModalProps> = ({show, onHide, contactId, currentTags, onTagsUpdated}) => {
  // State
  const [showCreateInput, setShowCreateInput] = useState(false)
  const [newTagName, setNewTagName] = useState('')
  const [selectedTagsToAdd, setSelectedTagsToAdd] = useState<TagOption[]>([])

  // Hook for tag management
  const {
    allTags,
    tagOptions,
    isLoadingTags,
    addTagsToContact,
    createNewTag,
    isUpdating,
    isCreating,
    error,
  } = useContactTags({
    contactId,
    currentTags,
    onTagsUpdated,
  })

  // Filter out tags that are already assigned to the contact
  const availableTagOptions = useMemo(() => {
    const currentTagIds = currentTags.map((t) => t._id).filter((id): id is string => Boolean(id))
    const currentTagNames = currentTags.map((t) => t.name.toLowerCase())

    return tagOptions.filter((option) => {
      // Filter out if tag ID matches
      if (currentTagIds.includes(option.value)) return false
      // Filter out if tag name matches (for tags without IDs)
      if (currentTagNames.includes(option.label.toLowerCase())) return false
      return true
    })
  }, [tagOptions, currentTags])

  // Reset state when modal closes
  useEffect(() => {
    if (!show) {
      setShowCreateInput(false)
      setNewTagName('')
      setSelectedTagsToAdd([])
    }
  }, [show])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleTagChange = (newValue: MultiValue<TagOption>) => {
    setSelectedTagsToAdd([...newValue])
  }

  const handleCreateClick = useCallback(() => {
    setShowCreateInput(true)
  }, [])

  const handleCreateTag = async () => {
    if (!newTagName.trim()) return

    const newTag = await createNewTag(newTagName.trim())
    if (newTag) {
      // Add the new tag to selected tags to add
      const newOption: TagOption = {
        value: newTag._id,
        label: newTag.name,
        color: newTag.color,
      }
      setSelectedTagsToAdd([...selectedTagsToAdd, newOption])
      setNewTagName('')
      setShowCreateInput(false)
    }
  }

  const handleSubmit = async () => {
    if (selectedTagsToAdd.length === 0) {
      onHide()
      return
    }

    const tagIds = selectedTagsToAdd.map((t) => t.value)
    const success = await addTagsToContact(tagIds)
    if (success) {
      onHide()
    }
  }

  const handleCancel = () => {
    setSelectedTagsToAdd([])
    onHide()
  }

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Modal show={show} onHide={handleCancel} centered dialogClassName={styles.tagsModalDialog}>
      <div className={styles.modalHeader}>
        <h2 className={styles.modalTitle}>Add Tags</h2>
        <div className={`btn btn-icon btn-sm`} onClick={handleCancel}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>

      <div className={styles.modalBody}>
        <label className={styles.fieldLabel}>Select tags to add</label>
        <Select<TagOption, true>
          isMulti
          value={selectedTagsToAdd}
          options={availableTagOptions}
          onChange={handleTagChange}
          styles={selectStyles}
          placeholder='Search or select tags...'
          isLoading={isLoadingTags}
          components={{MenuList: createCustomMenuList(handleCreateClick)}}
          classNamePrefix='tag-select'
          noOptionsMessage={() => 'No more tags available'}
          menuPortalTarget={document.body}
        />

        {/* Create new tag input */}
        {showCreateInput && (
          <div className={styles.createTagSection}>
            <label className={styles.fieldLabel}>Create new tag</label>
            <div className={styles.createTagInput}>
              <input
                type='text'
                value={newTagName}
                onChange={(e) => setNewTagName(e.target.value)}
                placeholder='Enter tag name...'
                className={styles.textInput}
                autoFocus
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleCreateTag()
                  }
                }}
              />
              <button
                className={styles.createButton}
                onClick={handleCreateTag}
                disabled={!newTagName.trim() || isCreating}
              >
                {isCreating ? (
                  <span className={styles.spinner}></span>
                ) : (
                  <i className='bi bi-check-lg'></i>
                )}
              </button>
              <div
                className={`btn btn-icon btn-sm`}
                onClick={() => {
                  setShowCreateInput(false)
                  setNewTagName('')
                }}
              >
                <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
              </div>
            </div>
          </div>
        )}

        {/* Error message */}
        {error && <div className={styles.errorMessage}>{error}</div>}
      </div>

      <div className={styles.modalFooter}>
        <button className={styles.cancelButton} onClick={handleCancel}>
          Cancel
        </button>
        <button
          className={styles.submitButton}
          onClick={handleSubmit}
          disabled={isUpdating || selectedTagsToAdd.length === 0}
        >
          {isUpdating ? (
            <>
              <span className={styles.spinner}></span>
              Adding...
            </>
          ) : (
            'Add Tags'
          )}
        </button>
      </div>
    </Modal>
  )
}

export default TagsModal